import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';

interface Props {
  id: string;
  showAvatar?: boolean;
  className?: string;
}

interface UserInfo {
  id: string;
  username: string;
  avatarUrl: string | null;
}

/**
 * Renders a Discord user by ID — resolves username + avatar via a cached
 * lookup on `/api/users/:id`. While loading, shows the ID in monospace so
 * layout doesn't jump when the real name arrives.
 *
 * React Query dedupes concurrent lookups of the same ID and caches for
 * 5 min in memory (matching the server-side TTL).
 */
export function UserMention({ id, showAvatar = true, className }: Props) {
  const { data } = useQuery({
    queryKey: ['user', id],
    queryFn: () => api<{ user: UserInfo }>(`/api/users/${id}`),
    staleTime: 5 * 60_000,
    enabled: !!id,
  });

  const user = data?.user;

  return (
    <span className={cn('inline-flex items-center gap-1.5 align-middle', className)}>
      {showAvatar && user?.avatarUrl && (
        <img
          src={user.avatarUrl}
          alt=""
          className="h-4 w-4 rounded-full"
          loading="lazy"
        />
      )}
      {user ? (
        <span className="truncate">{user.username}</span>
      ) : (
        <code className="text-xs text-muted-foreground">{id}</code>
      )}
    </span>
  );
}
